import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, PawPrint } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/format";
import { EASE } from "./Reveal";

export default function ProductCard({ product, index }) {
    const { add } = useCart();
    const [loaded, setLoaded] = useState(false);

    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.55, ease: EASE, delay: Math.min(index * 0.05, 0.3) }}
            data-testid={`product-card-${index + 1}`}
            className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-sand bg-cream-card shadow-[0_2px_12px_rgba(23,61,53,0.05)] transition-shadow duration-500 hover:shadow-[0_24px_48px_rgba(23,61,53,0.14)]"
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-sand/50">
                {!loaded && (
                    <div
                        className="absolute inset-0 animate-pulse bg-gradient-to-br from-sand/60 to-coral/10"
                        aria-hidden="true"
                    />
                )}
                <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    onLoad={() => setLoaded(true)}
                    onError={() => setLoaded(true)}
                    className={`h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${
                        loaded ? "opacity-100" : "opacity-0"
                    }`}
                />
                {product.badge && loaded && (
                    <span className="absolute left-3 top-3 rounded-full bg-forest/90 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-cream backdrop-blur">
                        {product.badge}
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-5">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-serif text-base font-bold leading-snug text-forest sm:text-lg">
                        {product.name}
                    </h3>
                </div>
                <p className="line-clamp-2 text-xs leading-relaxed text-graphite-muted sm:text-[13px]">
                    {product.description}
                </p>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-forest/40">{product.meta}</span>

                <div className="mt-auto flex items-end justify-between gap-3 pt-3">
                    <div className="flex flex-col">
                        <span className="flex items-baseline gap-2">
                            <span className="text-lg font-extrabold tracking-tight text-forest">
                                {formatBRL(product.price)}
                            </span>
                            {product.oldPrice && (
                                <span className="text-xs text-graphite-muted/70 line-through">
                                    {formatBRL(product.oldPrice)}
                                </span>
                            )}
                        </span>
                        <span className="mt-0.5 w-fit rounded-full bg-coral/15 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.14em] text-coral-dark">
                            Preço fictício
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid={`product-add-to-cart-${index + 1}`}
                        onClick={() => add(product)}
                        aria-label={`Adicionar ${product.name} à cesta demonstrativa`}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-forest text-cream transition-all duration-300 hover:scale-105 hover:bg-coral hover:text-forest"
                    >
                        <Plus className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </motion.article>
    );
}
